/**
 * Sites resource hooks. Mirrors the API endpoint shape from
 * `api/src/sites/sites.routes.ts`:
 *
 *   - GET    /api/admin/sites             — paginated list
 *   - GET    /api/admin/sites/:id         — single
 *   - POST   /api/admin/sites             — create
 *   - PATCH  /api/admin/sites/:id         — update
 *   - DELETE /api/admin/sites/:id         — soft delete
 *
 * Mutations invalidate `queryKeys.sites.all` rather than the specific list
 * key because pagination params on the active list view are unknown at
 * the mutation site — invalidating the parent prefix takes care of all
 * derived list keys.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { CreateSiteInput, SiteResponse, UpdateSiteInput } from "@ottopilot/shared";
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from "../lib/api";
import type { ApiClientError, PageMeta } from "../lib/api";
import { queryKeys } from "./queryKeys";

export type SitesListResult = {
  data: SiteResponse[];
  meta: PageMeta | undefined;
};

export function useSites(params: { page: number; pageSize: number }) {
  return useQuery<SitesListResult, ApiClientError>({
    queryKey: queryKeys.sites.list(params),
    queryFn: () =>
      apiList<SiteResponse[]>("/api/admin/sites", {
        query: { page: params.page, pageSize: params.pageSize },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useSite(id: string | undefined) {
  return useQuery<SiteResponse, ApiClientError>({
    queryKey: queryKeys.sites.detail(id ?? ""),
    queryFn: () => apiGet<SiteResponse>(`/api/admin/sites/${id ?? ""}`),
    enabled: Boolean(id),
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation<SiteResponse, ApiClientError, CreateSiteInput>({
    mutationFn: (input) => apiPost<SiteResponse>("/api/admin/sites", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

export function useUpdateSite(id: string) {
  const qc = useQueryClient();
  return useMutation<SiteResponse, ApiClientError, UpdateSiteInput>({
    mutationFn: (input) => apiPatch<SiteResponse>(`/api/admin/sites/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { id: string }>({
    mutationFn: ({ id }) => apiDelete<void>(`/api/admin/sites/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sites.all });
    },
  });
}
